import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponse } from '@/types';

class ApiService {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    // Use a local API URL for development
    const baseURL = import.meta.env.VITE_API_URL || 'https://fttf-world-api.bharath-f04.workers.dev';
    
    this.api = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      // Set a reasonable timeout
      timeout: 10000,
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      },
      (error) => {
        console.error('Request error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        // Log the error for debugging
        console.error('API Error:', error.message, error.response?.data);
        
        // Handle token expiration
        if (error.response?.status === 401) {
          // Clear token and redirect to login
          this.token = null;
          window.dispatchEvent(new CustomEvent('auth:logout'));
        }
        
        // Handle server errors
        if (error.response?.status >= 500) {
          console.warn('Server error, consider using demo data');
          window.dispatchEvent(new CustomEvent('api:server-error', { 
            detail: { url: error.config?.url, status: error.response?.status } 
          }));
        }
        
        // Handle network errors
        if (error.code === 'ECONNABORTED' || !error.response) {
          console.warn('Network error, consider using demo data');
          window.dispatchEvent(new CustomEvent('api:network-error', { 
            detail: { url: error.config?.url } 
          }));
        }
        
        return Promise.reject(error);
      }
    );
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.get<ApiResponse<T>>(url, config);
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.post<ApiResponse<T>>(url, data, config);
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.put<ApiResponse<T>>(url, data, config);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.delete<ApiResponse<T>>(url, config);
  }

  // File upload with progress tracking
  async uploadFile<T>(
    url: string,
    file: File,
    onProgress?: (percentage: number) => void,
    metadata?: Record<string, any>
  ): Promise<AxiosResponse<ApiResponse<T>>> {
    const formData = new FormData();
    formData.append('file', file);

    if (metadata) {
      Object.entries(metadata).forEach(([key, value]) => {
        formData.append(key, typeof value === 'string' ? value : JSON.stringify(value));
      });
    }

    return this.api.post<ApiResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentage);
        }
      },
    });
  }
}

export const api = new ApiService();

// API endpoints
export const getDrops = async () => {
  const response = await api.get('/drops');
  return response.data.data;
};

export const getStreams = async () => {
  const response = await api.get('/streams');
  return response.data.data;
};

export const getNFTDetails = async (id: string) => {
  const response = await api.get(`/nfts/${id}`);
  return response.data.data;
};

export const getNFTs = async () => {
  const response = await api.get('/nfts');
  return response.data.data;
};

export const getUserDrops = async () => {
  const response = await api.get('/user/drops');
  return response.data.data;
};

export const getUserStreams = async () => {
  const response = await api.get('/user/streams');
  return response.data.data;
};

export const getUserNFTs = async () => {
  const response = await api.get('/user/nfts');
  return response.data.data;
};